import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router";

import { Dispatch, bindActionCreators } from "redux";

import { ActionBar, ConfirmDialog, Overlay } from "@plex/react-components";

import { commonMessages } from "infrastructure/localization";
import { useLocalizedValues, useUserContext } from "ui/hooks";

import { dictionaryLinksActions } from "./DictionaryLinks.actions";
import { IDictionaryLinksState, IAssociatedMessage } from "./DictionaryLinks.state";
import DictionaryLinksForm from "./form/DictionaryLinksForm";

interface IDictionaryLinksProps {
  state: IDictionaryLinksState;
  messageId: string;
  loadAssociatedTerms: (messageId: string) => void;
  initializeDictionaryLinks: (messageId: string) => void;
  deleteAssociatedTerm: (message: IAssociatedMessage) => void;
}

const DictionaryLinks: React.FC<IDictionaryLinksProps> = ({ messageId, state, loadAssociatedTerms, initializeDictionaryLinks, deleteAssociatedTerm }) => {
  const localizedTitles = useLocalizedValues(() => ({
    question: commonMessages.info("deleteRecordQuestion"),
    askTitle: commonMessages.title("confirmationDialog"),
    deleteButtonText: commonMessages.action("delete"),
    noButton: commonMessages.option("no"),
    yesButton: commonMessages.option("yes")
  }));

  const history = useHistory();
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();
  const [isDialogOpened, setIsDialogOpened] = useState(false);

  useEffect(() => {
    loadAssociatedTerms(messageId);
    initializeDictionaryLinks(messageId);
  }, [loadAssociatedTerms, initializeDictionaryLinks, messageId]);

  useEffect(() => {
    if (state.shouldRedirect) {
      history.goBack();
    }
  }, [history, state.shouldRedirect]);

  const onDeleteDialogClick = useCallback(() => {
    setIsDialogOpened(true);
  }, [setIsDialogOpened]);

  const answers = [localizedTitles.yesButton, localizedTitles.noButton];
  const onDialogHide = useCallback(
    (answer: string) => {
      setIsDialogOpened(false);
      if (answer === localizedTitles.yesButton) {
        deleteAssociatedTerm({ messageId: messageId, term: state.toDeleteAssociatedTerm[0] });
      }
    },
    [deleteAssociatedTerm, messageId, state.toDeleteAssociatedTerm, localizedTitles]
  );

  return (
    <>
      <Overlay show={state.isLoading} showSpinner />
      <ActionBar>
        {roles.PlexTechnicalWriter && (
          <ActionBar.Button
            id="deleteButton"
            text={localizedTitles.deleteButtonText}
            title={localizedTitles.deleteButtonText}
            disabled={state.toDeleteAssociatedTerm.length === 0}
            onClick={onDeleteDialogClick}
          />
        )}
      </ActionBar>
      <DictionaryLinksForm messageId={messageId} associatedTerms={state.associatedTerms} />
      <ConfirmDialog
        show={isDialogOpened}
        onHide={onDialogHide}
        question={localizedTitles.question}
        title={localizedTitles.askTitle}
        defaultAnswer={answers[1]}
        answers={answers}
      ></ConfirmDialog>
    </>
  );
};

export default connect(
  (state: { dictionaryLinks: IDictionaryLinksState }) => {
    return {
      state: state.dictionaryLinks
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        loadAssociatedTerms: dictionaryLinksActions.loadAssociatedTerms,
        deleteAssociatedTerm: dictionaryLinksActions.deleteAssociatedTerm,
        initializeDictionaryLinks: dictionaryLinksActions.initializeDictionaryLinks
      },
      dispatch
    )
)(DictionaryLinks);
